/**
 * Who gets told a pull finished (`specs/product/listing-content-panel`,
 * `AUT-231`, review of PR #254), extended by
 * `specs/product/ai-score-listing-refresh-shares-the-cooldown`
 * (`AUT-416`, `decisions/a-listing-toast-is-addressed-by-user-and-tab-mark.md`)
 * to cover an audit's refresh as well as a user's own.
 *
 * `.listing.status.updated` broadcasts on `organization.{orgId}` and every
 * open tab in the organization receives every terminal pull. That is
 * exactly right for cache invalidation -- everyone's panel should show the
 * new content -- and exactly wrong for the toast: without a filter, a
 * colleague's pull, or a colleague's audit, interrupts everyone else on
 * every screen.
 *
 * Two independent marks live here, both `sessionStorage` (the mark belongs
 * to the tab that clicked or ran the audit, and a second tab of the same
 * browser did neither):
 *
 * - **The listing mark** (`listing:pull-initiated:{productId}`) -- written
 *   by the panel's own `POST`, on a `202` (`own`) and on a `409` (`any`,
 *   today's rule: the user asked for this content and the in-flight pull,
 *   whoever started it, serves the ask).
 * - **The audit mark** (`listing:audit-initiated:{ASIN}`) -- written by
 *   `useCreateScorecard`'s `onSuccess`, the one mutation both
 *   audit-starting screens use. Keyed by ASIN, not product id, because
 *   `/spi` has no product id and the listing broadcast carries `asin`.
 *
 * Both marks are read against the broadcast's `triggered_by_user_id`,
 * classified into a `ListingEventAuthor` by the caller
 * (`listing.events.ts`, `WEB-002`) before either `consume…` function is
 * called.
 */

export type ListingMarkScope = 'own' | 'any';
export type ListingEventAuthor = 'mine' | 'not_mine' | 'unknown';

const KEY_PREFIX = 'listing:pull-initiated:';
const AUDIT_KEY_PREFIX = 'listing:audit-initiated:';

/**
 * How long the listing mark is honoured. `PULLING_DEADLINE_SECONDS` on the
 * backend's `ProductListing` is 600 s: past that the claim itself is
 * reaped and a fresh `POST` supersedes it, so a mark older than this can
 * only belong to a pull whose broadcast never arrived (Reverb down, the
 * tab asleep). Left unbounded, that dead mark would spend itself on the
 * *next* broadcast this tab sees -- which could well be a colleague's.
 */
const MARK_TTL_MS = 600_000;

/**
 * How long the audit mark is honoured. Longer than the listing mark's
 * 600 s because it is written at `POST /api/scorecards`, before the queue
 * delay `GenerateScorecardJob` sits behind and its own `$timeout = 600`
 * plus `ProductListing::PULLING_DEADLINE_SECONDS = 600`.
 */
const AUDIT_MARK_TTL_MS = 1_200_000;

const keyFor = (productId: number) => `${KEY_PREFIX}${productId}`;
const auditKeyFor = (asin: string) => `${AUDIT_KEY_PREFIX}${asin.trim().toUpperCase()}`;

interface StoredMark {
  markedAt: number;
  scope: ListingMarkScope;
}

/**
 * Reads either the current `{ markedAt, scope }` shape or a bare numeric
 * timestamp written by a tab that loaded before this change -- read as
 * `any`, which is what an unscoped mark meant. `null` for anything else
 * (corrupted storage), never a throw.
 */
function parseMark(raw: string): StoredMark | null {
  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed === 'number') {
      return Number.isFinite(parsed) ? { markedAt: parsed, scope: 'any' } : null;
    }

    if (parsed && typeof parsed === 'object') {
      const candidate = parsed as { markedAt?: unknown; scope?: unknown };
      const markedAt = typeof candidate.markedAt === 'number' ? candidate.markedAt : NaN;
      if (!Number.isFinite(markedAt)) return null;
      return { markedAt, scope: candidate.scope === 'own' ? 'own' : 'any' };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Called from `usePullListing.onSuccess` on a `202` (scope `own`) and from
 * the panel's `409` branch (scope `any`). Deliberately not called on
 * `mutate`: a `409` or `429` refusal at the network layer queues nothing
 * for a `429`, so there is no broadcast for the mark to meet and it would
 * sit here until it expired.
 */
export function markPullInitiated(productId: number, scope: ListingMarkScope = 'any'): void {
  try {
    sessionStorage.setItem(keyFor(productId), JSON.stringify({ markedAt: Date.now(), scope }));
  } catch {
    // sessionStorage unavailable (private browsing, storage disabled).
    // The pull still runs and the panel still refreshes -- this tab just
    // will not toast, which is the quiet failure of the two.
  }
}

/**
 * Reads the listing mark, clears it when it is spent, and reports whether
 * this event should toast in this tab.
 *
 * - No mark, or one older than `MARK_TTL_MS`: `false` (the latter also
 *   clears it).
 * - `any`: every author spends it.
 * - `own`: `mine` and `unknown` spend it (today's rule for an event that
 *   cannot say who asked); `not_mine` -- a present author that is not this
 *   account, a deleted user's `null` included -- leaves it in place for the
 *   event that really is this tab's own.
 */
export function consumePullInitiated(
  productId: number,
  author: ListingEventAuthor = 'unknown',
): boolean {
  try {
    const key = keyFor(productId);
    const raw = sessionStorage.getItem(key);
    if (raw === null) return false;

    const mark = parseMark(raw);
    if (!mark) {
      sessionStorage.removeItem(key);
      return false;
    }

    if (Date.now() - mark.markedAt > MARK_TTL_MS) {
      sessionStorage.removeItem(key);
      return false;
    }

    if (mark.scope === 'any') {
      sessionStorage.removeItem(key);
      return true;
    }

    if (author === 'not_mine') return false;

    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Called from `useCreateScorecard`'s `onSuccess`, keyed by the response's
 * `asin` -- normalised the same way on write and read, since `/spi` and
 * the product-page audit screen may not agree on case or whitespace.
 */
export function markAuditInitiated(asin: string): void {
  try {
    sessionStorage.setItem(auditKeyFor(asin), String(Date.now()));
  } catch {
    // See markPullInitiated: the audit still runs, this tab just won't
    // toast on its listing refresh.
  }
}

/**
 * `true` (and removed) only when this tab ran the audit (`author ===
 * 'mine'`) and the mark has not outlived `AUDIT_MARK_TTL_MS`. Any other
 * author leaves the mark untouched -- it is not this event's to spend --
 * so a later `mine` event, or `clearAuditInitiated` on the scorecard's own
 * terminal broadcast, is what finally removes it.
 */
export function consumeAuditInitiated(asin: string, author: ListingEventAuthor): boolean {
  try {
    const key = auditKeyFor(asin);
    const raw = sessionStorage.getItem(key);
    if (raw === null) return false;

    if (author !== 'mine') return false;

    sessionStorage.removeItem(key);

    const markedAt = Number(raw);
    if (!Number.isFinite(markedAt)) return false;

    return Date.now() - markedAt <= AUDIT_MARK_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Called on the scorecard's own terminal broadcast (`completed` /
 * `failed`) for this ASIN, in `ProtectedRoute.tsx` (`WEB-002`), beside the
 * existing `localStorage` removal there. An audit that never touches the
 * listing (a non-hero ASIN, a listing inside its window, a failed
 * resolve) leaves no listing terminal event to consume this mark, so it
 * must be cleared explicitly rather than left to expire.
 */
export function clearAuditInitiated(asin: string): void {
  try {
    sessionStorage.removeItem(auditKeyFor(asin));
  } catch {
    // Nothing to do -- the mark will simply expire on its own TTL.
  }
}
